/**
 * Time Capsule API Service
 *
 * Handles all API calls to the Time Capsule Analytics API
 */

import {
    API_BASE_URL,
    API_TIMEOUT_MS,
    API_RETRY_ATTEMPTS,
    API_RETRY_DELAY_MS,
    MAX_SELFIE_SIZE_KB,
    AgeGroupValue,
    VISITOR_COOKIE_EXPIRATION_MS,
    VISITOR_COOKIE_WARNING_MS,
    getCookieExpirationHours
} from '../utils/constants';
import { TrackingEvent } from "../utils/eventTracking";
import { store } from '../store';

export interface VisitorSignupData {
    name: string;
    email: string;
    ageGroup: AgeGroupValue;
    nationality: string;
    language: string;
}

export interface VisitorSignupResponse {
    visitorId: string;
    message?: string;
    // Add other response fields as needed based on actual API response
}

export interface SelfieUploadResponse {
    id: string;
    filename: string;
    message: string;
    // Add other response fields as needed
}

export interface ApiError {
    message: string;
    status: number;
    details?: any;
}

// ZonesStatus API interfaces
export interface ZoneStatusData {
    status: 'available' | 'processing' | 'error';
    claimedBy?: string;
    payload?: any; // Flexible payload structure to match API
}

export interface ZoneStatusResponse {
    zoneId: string;
    status: 'available' | 'processing' | 'error';
    payload?: any; // Can be any object structure, as seen in API response
    claimedBy?: string | null;
    updatedAt: string;
}

export interface ZonePayloadData {
    payload: any; // Flexible payload structure to match API
}

class TimeCapsuleApiService {
    /**
     * Get authentication headers for endpoints that need header-based auth
     * Based on backend notes: zones-status/update, edited-selfie upload, event/track-lidar need header auth
     */
    private getAuthHeaders(endpoint: string, visitorId?: string): Record<string, string> {
        // Endpoints that need header-based authentication
        const needsHeaderAuth = [
            '/zones-status/',
            '/visitors/selfie-edited',
            '/events/track'
        ].some(path => endpoint.includes(path));

        if (needsHeaderAuth) {
            // Get visitor ID from parameter (primary) or localStorage (fallback)
            let authVisitorId = visitorId || localStorage.getItem('visitorId') || '';
            
            if (authVisitorId) {
                console.log(`🔐 Adding auth headers for ${endpoint}:`, {
                    visitorId: `${authVisitorId.substring(0, 8)}...`,
                    headers: ['Authorization', 'X-Visitor-ID'],
                    source: visitorId ? 'parameter' : 'localStorage'
                });
                
                return {
                    'Authorization': `Bearer ${authVisitorId}`,
                    'X-Visitor-ID': authVisitorId
                };
            } else {
                console.warn(`⚠️ No visitor ID found for auth headers on ${endpoint}`);
            }
        }

        return {};
    }

    /**
     * Determine credentials strategy based on endpoint
     */
    private getCredentialsStrategy(endpoint: string): RequestCredentials {
        // Endpoints that explicitly don't need cookies (per API docs)
        const noCookieEndpoints = [
            '/visitors/selfie-edited',
            '/health'
        ];

        // Endpoints that need header auth instead of cookies
        // const headerAuthEndpoints = [
            // '/zones-status/',
        // ];

        if (noCookieEndpoints.some(path => endpoint.includes(path))) {
            return 'omit';
        }

        // Default to include credentials for other endpoints
        return 'include';
    }

    /**
     * Simple retry wrapper for API requests with exponential backoff
     */
    private async makeRequestWithRetry<T>(
        endpoint: string,
        options: RequestInit = {},
        retryCount: number = 0,
        visitorId?: string
    ): Promise<T> {
        try {
            return await this.makeRequest<T>(endpoint, options, visitorId);
        } catch (error) {
            const apiError = error as ApiError;

            // Retry on network errors or 5xx server errors
            const shouldRetry = (
                apiError.status === 0 || // Network error
                apiError.status === 408 || // Timeout
                (apiError.status >= 500 && apiError.status < 600) // Server errors
            );

            if (shouldRetry && retryCount < API_RETRY_ATTEMPTS - 1) {
                console.warn(`API request failed (attempt ${retryCount + 1}/${API_RETRY_ATTEMPTS}):`, apiError.message);

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, API_RETRY_DELAY_MS * (retryCount + 1)));

                return this.makeRequestWithRetry<T>(endpoint, options, retryCount + 1, visitorId);
            }

            throw error;
        }
    }

    /**
     * Make API request with proper authentication strategy
     */
    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        visitorId?: string
    ): Promise<T> {
        const url = `${API_BASE_URL}${endpoint}`;

        // Get authentication headers and credentials strategy
        const authHeaders = this.getAuthHeaders(endpoint, visitorId);
        const credentials = this.getCredentialsStrategy(endpoint);

        // Don't set Content-Type for FormData - let browser handle it
        const isFormData = options.body instanceof FormData;
        const defaultHeaders = isFormData ? 
            { ...authHeaders, ...options.headers } : 
            { 'Content-Type': 'application/json', ...authHeaders, ...options.headers };

        console.log(`🔄 API Request:`, {
            method: options.method || 'GET',
            url,
            credentials,
            hasAuthHeaders: Object.keys(authHeaders).length > 0,
            authHeaders: Object.keys(authHeaders),
            isFormData,
            visitorIdProvided: !!visitorId
        });

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

        try {
            const response = await fetch(url, {
                ...options,
                headers: defaultHeaders,
                signal: controller.signal,
                credentials,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch {
                    errorData = { message: errorText };
                }

                console.error(`❌ API Error (${response.status}):`, {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    error: errorData,
                    credentials,
                    hadAuthHeaders: Object.keys(authHeaders).length > 0,
                    visitorIdProvided: !!visitorId
                });

                throw {
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    details: errorData
                } as ApiError;
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                console.log(`✅ API Success:`, { url, data });
                return data;
            } else {
                const data = await response.text() as any;
                console.log(`✅ API Success (text):`, { url, data });
                return data;
            }
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof DOMException && error.name === 'AbortError') {
                throw {
                    message: `Request timeout after ${API_TIMEOUT_MS / 1000} seconds`,
                    status: 408,
                    details: error
                } as ApiError;
            }

            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw {
                    message: 'Network error - please check your internet connection',
                    status: 0,
                    details: error
                } as ApiError;
            }
            throw error;
        }
    }

    /**
     * Sign up a new visitor
     */
    async signupVisitor(data: VisitorSignupData): Promise<VisitorSignupResponse> {
        console.log('🚀 Starting visitor signup process:', {
            endpoint: '/visitors/signup',
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
            isAndroid: /Android/.test(navigator.userAgent),
            data: {
                name: data.name,
                email: data.email,
                ageGroup: data.ageGroup,
                nationality: data.nationality,
                language: data.language
            }
        });

        try {
            const result = await this.makeRequestWithRetry<VisitorSignupResponse>('/visitors/signup', {
                method: 'POST',
                body: JSON.stringify(data),
            });

            console.log('✅ Visitor signup successful:', {
                visitorId: result.visitorId,
                message: result.message
            });

            return result;
        } catch (error) {
            console.error('❌ Visitor signup failed:', {
                error,
                userAgent: navigator.userAgent,
                isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
                isAndroid: /Android/.test(navigator.userAgent)
            });
            throw error;
        }
    }

    /**
     * Check visitor cookie status and expiration
     * Returns information about the visitor cookie state
     */
    checkVisitorCookieStatus(): { 
        hasCookie: boolean; 
        cookieAge: number | null; 
        cookieAgeHours: number | null; 
        isExpired: boolean; 
        willExpireSoon: boolean;
        cookieValue?: string;
    } {
        const cookies = document.cookie.split(';').reduce((acc, cookie) => {
            const [key, value] = cookie.trim().split('=');
            acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const cookieVisitorId = cookies['v'];
        const visitorCreatedAt = localStorage.getItem('visitorCreatedAt');
        const cookieAge = visitorCreatedAt ? Date.now() - parseInt(visitorCreatedAt) : null;
        const cookieAgeHours = cookieAge ? Math.floor(cookieAge / (1000 * 60 * 60)) : null;
        
        // Enhanced expiration detection with more robust logic
        // Only consider cookies expired if BOTH conditions are true:
        // 1. Cookie is actually missing from browser
        // 2. localStorage timestamp suggests it should be expired
        const timestampSuggestsExpired = cookieAge ? cookieAge > VISITOR_COOKIE_EXPIRATION_MS : false;
        const cookieActuallyMissing = !cookieVisitorId;
        const isExpired = cookieActuallyMissing && timestampSuggestsExpired;
        
        // Warning threshold is more lenient - only warn based on timestamp
        const willExpireSoon = cookieAge ? cookieAge > VISITOR_COOKIE_WARNING_MS : false;
        
        return {
            hasCookie: !!cookieVisitorId,
            cookieAge,
            cookieAgeHours,
            isExpired,
            willExpireSoon,
            cookieValue: cookieVisitorId ? `${cookieVisitorId.substring(0, 8)}...` : undefined
        };
    }

    /**
     * Get current visitor details using /visitors/me endpoint
     * This endpoint uses cookie-based authentication and depends on the visitor ID ("v") cookie
     * Note: Backend sets cookie expiration (configurable via environment variables)
     */
    async getCurrentVisitor(): Promise<any> {
        console.log('🔍 Getting current visitor data using /visitors/me');
        
        // Check visitor cookie status using utility function
        const cookieStatus = this.checkVisitorCookieStatus();
        
        console.log('🍪 Cookie check for /visitors/me:', cookieStatus);
        
        if (!cookieStatus.hasCookie) {
            console.warn('⚠️ No visitor ID cookie found - /visitors/me will likely fail');
            throw {
                message: 'No visitor ID cookie found. User may not be registered or cookie expired.',
                status: 401,
                details: { missingCookie: 'v', reason: 'no_cookie' }
            } as ApiError;
        }
        
        // Warn if cookie is likely expired based on our tracking
        if (cookieStatus.isExpired) {
                            console.warn(`⏰ Visitor cookie appears to be expired (older than ${getCookieExpirationHours()} hours)`);
            // Don't throw here, let the API call fail naturally for more accurate error handling
        } else if (cookieStatus.willExpireSoon) {
            console.warn(`⏰ Visitor cookie is getting old (${cookieStatus.cookieAgeHours} hours) - may expire soon`);
        }
        
        try {
            const response = await this.makeRequest<any>('/visitors/me', {
                method: 'GET',
            });
            
            console.log('✅ Successfully fetched current visitor data');
            return response;
        } catch (error) {
            console.error('❌ Failed to fetch current visitor data:', error);
            
            // If it's a 401/403 error, it might be due to cookie issues
            const apiError = error as ApiError;
            if (apiError.status === 401 || apiError.status === 403) {
                if (cookieStatus.isExpired) {
                    console.warn(`🍪 Authentication failed - visitor ID cookie has expired (older than ${getCookieExpirationHours()} hours)`);
                    throw {
                        ...apiError,
                        message: 'Your session has expired. Please register again.',
                        details: { ...apiError.details, reason: 'cookie_expired', cookieAgeHours: cookieStatus.cookieAgeHours }
                    } as ApiError;
                } else {
                    console.warn('🍪 Authentication failed - visitor ID cookie may be invalid');
                    throw {
                        ...apiError,
                        message: 'Authentication failed. Your session may be invalid.',
                        details: { ...apiError.details, reason: 'cookie_invalid' }
                    } as ApiError;
                }
            }
            
            throw error;
        }
    }

    /**
     * Get visitor details by ID
     * Note: This endpoint requires cookie-based authentication where the visitor ID in the cookie
     * must match the visitor ID in the URL parameter
     * 
     * @deprecated Consider using getCurrentVisitor() instead for better reliability
     */
    async getVisitor(id: string): Promise<any> {
        console.log(`🔍 Getting visitor data for ID: ${id}`);
        
        // Check if we have a visitor ID cookie that matches
        const cookies = document.cookie.split(';').reduce((acc, cookie) => {
            const [key, value] = cookie.trim().split('=');
            acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const cookieVisitorId = cookies['v'];
        console.log('🍪 Cookie analysis:', {
            requestedId: id,
            cookieVisitorId: cookieVisitorId,
            match: cookieVisitorId === id,
            allCookies: Object.keys(cookies)
        });
        
        if (cookieVisitorId && cookieVisitorId !== id) {
            console.warn('⚠️ Visitor ID mismatch detected:', {
                reduxVisitorId: id,
                cookieVisitorId: cookieVisitorId,
                recommendation: 'Using cookie visitor ID for API call'
            });
            
            // Use the cookie visitor ID instead of the Redux one for the API call
            // This handles the case where Redux has a stale visitor ID
            return this.makeRequest<any>(`/visitors/${cookieVisitorId}`, {
                method: 'GET',
            });
        }
        
        return this.makeRequest<any>(`/visitors/${id}`, {
            method: 'GET',
        });
    }

    /**
     * Upload visitor selfie
     */
    async uploadSelfie(
        visitorId: string,
        selfieBlob: Blob,
        filename: string = 'selfie.jpg'
    ): Promise<SelfieUploadResponse> {
        // Validate file size
        const fileSizeKB = selfieBlob.size / 1024;
        if (fileSizeKB > MAX_SELFIE_SIZE_KB) {
            throw {
                message: `File size (${Math.round(fileSizeKB)}KB) exceeds maximum allowed size (${MAX_SELFIE_SIZE_KB}KB)`,
                status: 413,
                details: {fileSizeKB, maxSizeKB: MAX_SELFIE_SIZE_KB}
            } as ApiError;
        }

        const formData = new FormData();
        formData.append('photo', selfieBlob, filename);
        // Note: /visitors/selfie endpoint doesn't expect visitorId field

        console.log('📤 Uploading selfie with FormData:', {
            visitorId,
            visitorIdType: typeof visitorId,
            visitorIdLength: visitorId?.length,
            filename,
            fileSizeKB: Math.round(fileSizeKB),
            blobType: selfieBlob.type
        });

        // Debug FormData contents
        console.log('📤 FormData entries:');
        console.log(`  visitorId: ${formData.get('visitorId')}`);
        console.log(`  selfie: [File] ${(formData.get('selfie') as File)?.size || 'unknown'} bytes`);

        // Use makeRequest method which now handles FormData properly
        return await this.makeRequest<SelfieUploadResponse>('/visitors/selfie', {
            method: 'POST',
            body: formData,
        });
    }

    /**
     * Upload edited selfie (no cookie required)
     */
    async uploadEditedSelfie(
        visitorId: string,
        selfieBlob: Blob,
        filename: string = 'edited-selfie.jpg'
    ): Promise<SelfieUploadResponse> {
        const formData = new FormData();
        formData.append('visitorId', visitorId);
        formData.append('editedSelfie', selfieBlob, filename);

        return this.makeRequest<SelfieUploadResponse>('/visitors/selfie-edited', {
            method: 'POST',
            body: formData,
        }, visitorId);
    }

    /**
     * Track an event
     */
    async trackEvent(eventType: string, {userMetadata, zone, metadata, timestamp}: TrackingEvent, visitorId?: string): Promise<any> {

        const testTimestamp = timestamp ? {
            _testTimestamp: timestamp
        } : {}

        return this.makeRequest<any>('/events/track', {
            method: 'POST',
            body: JSON.stringify({
                eventType,
                zone,
                metadata: {
                    eventType,
                    ...userMetadata,
                    ...metadata as Object,
                },
                ...testTimestamp,
            }),
        }, visitorId);
    }

    /**
     * Get analytics data
     */
    async getAnalytics(filters?: any): Promise<any> {
        const queryParams = filters ? '?' + new URLSearchParams(filters).toString() : '';
        return this.makeRequest<any>(`/events/analytics${queryParams}`, {
            method: 'GET',
        });
    }

    /**
     * Update zone status
     */
    async updateZoneStatus(zoneId: string, statusData: ZoneStatusData, visitorId?: string): Promise<ZoneStatusResponse> {
        return this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}`, {
            method: 'PUT',
            body: JSON.stringify(statusData),
        }, visitorId);
    }

    /**
     * Get zone status with enhanced client-side session mapping
     * Since API doesn't use our visitorId parameter, we need client-side mapping
     */
    async getZoneStatus(zoneId: string, visitorId?: string): Promise<ZoneStatusResponse> {
        const zoneStatus = await this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}`, {
            method: 'GET',
        }, visitorId);

        console.log(`🔍 Raw zone status from API for ${zoneId}:`, {
            status: zoneStatus.status,
            claimedBy: zoneStatus.claimedBy,
            requestingVisitorId: visitorId,
            directMatch: zoneStatus.claimedBy === visitorId
        });

        // Enhanced client-side session mapping (async to prevent UI blocking)
        if (visitorId) {
            // Always check for mapping when we have a visitorId, regardless of API claimedBy value
            const mappedVisitorId = await new Promise<string | null>((resolve) => {
                setTimeout(() => {
                    resolve(localStorage.getItem(`zoneClaimMapping_${zoneId}`));
                }, 0);
            });
            
            console.log(`🔍 Client-side mapping check for ${zoneId}:`, {
                apiClaimedBy: zoneStatus.claimedBy,
                requestingVisitorId: visitorId,
                storedMapping: mappedVisitorId,
                mappingExists: !!mappedVisitorId,
                mappingMatches: mappedVisitorId === visitorId,
                apiClaimedByIsNull: zoneStatus.claimedBy === null,
                directMatch: zoneStatus.claimedBy === visitorId
            });
            
            // Apply mapping if:
            // 1. We have a stored mapping for this visitor, AND
            // 2. Either the API claimedBy doesn't match, OR the API claimedBy is null/undefined
            if (mappedVisitorId === visitorId && (zoneStatus.claimedBy !== visitorId || !zoneStatus.claimedBy)) {
                console.log(`🔄 Applying client-side session mapping for ${zoneId}:`, {
                    reason: zoneStatus.claimedBy ? 'API uses different visitor ID system' : 'API claimedBy is null/undefined',
                    originalClaimedBy: zoneStatus.claimedBy,
                    mappingToVisitorId: visitorId,
                    status: zoneStatus.status
                });

                // Return modified zone status with correct visitor ID for client-side consistency
                return {
                    ...zoneStatus,
                    claimedBy: visitorId,
                    // Add a flag to indicate this is client-side mapped
                    _clientSideMapped: true,
                    _originalClaimedBy: zoneStatus.claimedBy
                } as ZoneStatusResponse & { _clientSideMapped?: boolean; _originalClaimedBy?: string };
            } else {
                console.log(`ℹ️ No client-side mapping applied for ${zoneId}:`, {
                    hasStoredMapping: !!mappedVisitorId,
                    mappingMatches: mappedVisitorId === visitorId,
                    apiClaimedBy: zoneStatus.claimedBy,
                    directMatch: zoneStatus.claimedBy === visitorId,
                    result: zoneStatus.claimedBy === visitorId ? 'Direct API match' : 'No mapping available'
                });
            }
        }

        return zoneStatus;
    }

    /**
     * Claim zone status to 'processing'
     * Enhanced with robust client-side mapping since API ignores visitorId parameter
     */
    async claimZoneStatus(zoneId: string, visitorId?: string): Promise<ZoneStatusResponse> {
        console.log(`🎯 Claiming zone status for: ${zoneId}`, { 
            hasVisitorId: !!visitorId,
            visitorIdPreview: visitorId ? `${visitorId.substring(0, 8)}...` : 'none',
            userType: visitorId ? 'registered' : 'anonymous'
        });

        // Step 1: Make the actual claim API call
        await this.makeRequest<any>(`/zones-status/${zoneId}/claim`, { 
            method: 'POST' 
        }, visitorId); // Pass visitorId for auth headers if available

        console.log(`✅ Zone claim successful for ${zoneId}`);

        // Step 2: Store client-side mapping for UI consistency (non-blocking)
        let effectiveVisitorId = visitorId;
        
        if (visitorId) {
            // Registered user - use their actual visitor ID
            const storageKey = `zoneClaimMapping_${zoneId}`;
            localStorage.setItem(storageKey, visitorId);
            
            // Verify storage was successful
            const storedValue = localStorage.getItem(storageKey);
            console.log(`💾 Stored client-side mapping for registered user ${zoneId}:`, {
                visitorId: `${visitorId.substring(0, 8)}...`,
                storageKey,
                storedSuccessfully: storedValue === visitorId,
                storedValue: storedValue ? `${storedValue.substring(0, 8)}...` : null,
                purpose: 'UI consistency since API uses different visitor ID system'
            });
            
            if (storedValue !== visitorId) {
                console.error(`❌ localStorage mapping storage failed for ${zoneId}:`, {
                    attempted: visitorId,
                    actual: storedValue,
                    error: 'localStorage may be disabled or full'
                });
            }
        } else {
            // Anonymous user - generate temporary session ID for UI consistency
            const anonymousSessionKey = `anonymousSession_${zoneId}`;
            let anonymousSessionId = localStorage.getItem(anonymousSessionKey);
            
            if (!anonymousSessionId) {
                // Generate a temporary session ID for this anonymous user
                anonymousSessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem(anonymousSessionKey, anonymousSessionId);
                console.log(`🆔 Generated anonymous session ID for ${zoneId}:`, {
                    sessionId: anonymousSessionId,
                    purpose: 'Track anonymous user zone ownership for UI consistency'
                });
            }
            
            // Store mapping for anonymous user
            const storageKey = `zoneClaimMapping_${zoneId}`;
            localStorage.setItem(storageKey, anonymousSessionId);
            effectiveVisitorId = anonymousSessionId;
            
            console.log(`💾 Stored client-side mapping for anonymous user ${zoneId}:`, {
                anonymousSessionId,
                storageKey,
                purpose: 'UI consistency for anonymous zone claims'
            });
        }

        // Step 3: Get the actual zone status immediately (this is what Redux expects)
        const zoneStatus = await this.getZoneStatus(zoneId, effectiveVisitorId);
        
        console.log(`🔍 Final zone status for ${zoneId}:`, {
            status: zoneStatus.status,
            claimedBy: zoneStatus.claimedBy,
            effectiveVisitorId,
            originalVisitorId: visitorId,
            userType: visitorId ? 'registered' : 'anonymous',
            isMatch: zoneStatus.claimedBy === effectiveVisitorId,
            hasMappingFlag: !!(zoneStatus as any)._clientSideMapped,
            originalClaimedBy: (zoneStatus as any)._originalClaimedBy,
            result: zoneStatus.claimedBy === effectiveVisitorId ?
                '✅ SUCCESS - Client-side mapping working' :
                '❌ FAILED - Mapping issue detected'
        });

        // Step 4: Do additional async logging without blocking (optional)
        setTimeout(async () => {
            try {
                console.log(`📊 Post-claim verification for ${zoneId} completed`);
            } catch (error) {
                console.error(`❌ Post-claim verification failed for ${zoneId}:`, error);
            }
        }, 0);

        return zoneStatus;
    }

    /**
     * Update zone status payload
     * Used by visitor/mobile client to update payload when zone is claimed
     */
    async updateZonePayload(zoneId: string, payloadData: ZonePayloadData, visitorId?: string): Promise<ZoneStatusResponse> {
        console.log(`🔄 Updating zone payload for ${zoneId}:`, {
            endpoint: `/zones-status/${zoneId}/payload`,
            method: 'PUT',
            payload: payloadData,
            option: payloadData.payload?.option,
            visitorId: visitorId ? `${visitorId.substring(0, 8)}...` : 'none',
            visitorIdProvided: !!visitorId,
            visitorIdLength: visitorId?.length,
            note: 'Using header-based authentication'
        });

        // Double-check what auth headers will be generated
        const authHeaders = this.getAuthHeaders(`/zones-status/${zoneId}/payload`, visitorId);
        console.log(`🔐 Auth headers for payload update:`, {
            headers: authHeaders,
            hasVisitorId: !!visitorId,
            willHaveAuth: Object.keys(authHeaders).length > 0
        });

        return this.makeRequest<ZoneStatusResponse>(`/zones-status/${zoneId}/payload`, {
            method: 'PUT',
            body: JSON.stringify(payloadData),
        }, visitorId);
    }

    /**
     * Health check
     */
    async healthCheck(): Promise<any> {
        return this.makeRequest<any>('/health', {
            method: 'GET',
        });
    }
}

// Export singleton instance
export const timeCapsuleApi = new TimeCapsuleApiService();

/**
 * Enhanced visitor ID getter with smart fallback strategy
 * Priority: Cookie (primary) -> Redux (backup) -> localStorage (emergency)
 */
export const getVisitorIdWithFallback = (): string | null => {
    // Get cookie visitor ID
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
    }, {} as Record<string, string>);
    const cookieVisitorId = cookies['v'];
    
    // Get Redux visitor ID (using actual store instance)
    let reduxVisitorId: string | null = null;
    try {
        const state = store.getState();
        reduxVisitorId = state.user.visitorId || null;
    } catch (error) {
        console.warn('Could not access Redux store for visitor ID fallback:', error);
    }
    
    // Get localStorage visitor ID
    const localStorageVisitorId = localStorage.getItem('visitorId');
    
    // Apply priority fallback
    const finalVisitorId = cookieVisitorId || reduxVisitorId || localStorageVisitorId;
    
    return finalVisitorId || null;
};

/**
 * Clear all zone claim mappings from localStorage
 * This should be called when user logs out or resets to prevent stale zone claims
 * Now also clears anonymous session IDs
 */
export const clearZoneClaimMappings = (): void => {
    const keysToRemove: string[] = [];
    
    // Find all zoneClaimMapping and anonymousSession keys in localStorage
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('zoneClaimMapping_') || key.startsWith('anonymousSession_'))) {
            keysToRemove.push(key);
        }
    }
    
    // Remove all found zone claim mappings and anonymous sessions
    keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        const keyType = key.startsWith('zoneClaimMapping_') ? 'zone mapping' : 'anonymous session';
        console.log(`🧹 Cleared ${keyType}: ${key}`);
    });
    
    if (keysToRemove.length > 0) {
        const mappingCount = keysToRemove.filter(k => k.startsWith('zoneClaimMapping_')).length;
        const sessionCount = keysToRemove.filter(k => k.startsWith('anonymousSession_')).length;
        console.log(`✅ Cleared ${mappingCount} zone mappings and ${sessionCount} anonymous sessions from localStorage`);
    } else {
        console.log('ℹ️ No zone claim mappings or anonymous sessions found to clear');
    }
};

// Utility function to convert data URL to Blob
export function dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], {type: mime});
}